import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {useFormik} from 'formik';
import {Box, Button, DialogTitle, InputLabel, Typography} from '@mui/material';
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import List from '@ckeditor/ckeditor5-list/src/list';
import Link from '@ckeditor/ckeditor5-link/src/link';
import AutoLink from '@ckeditor/ckeditor5-link/src/autolink';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import Font from '@ckeditor/ckeditor5-font/src/font';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import Placeholder from "ckeditor5-placeholder";
import Image from '@ckeditor/ckeditor5-image/src/image';
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import {COMPLETED_CALL, MISSED_CALL} from '../../Utils/constants';
import {BASE_DOMAIN_URL} from '../../../API/util';

const editorConfiguration = {
    plugins: [List, Placeholder, Essentials, Bold, Italic, Paragraph, SimpleUploadAdapter, ImageUpload, Image, Heading, Link, AutoLink, Indent, IndentBlock, Font, Alignment, Underline, Strikethrough, Table, TableToolbar, HorizontalLine, BlockQuote],
    toolbar: ['heading', '|', 'Placeholder', '|', 'fontColor', 'fontBackgroundColor', '|', 'outdent', 'indent', '|', 'bold', 'italic', 'underline', 'strikethrough', '|', 'link', 'horizontalLine', 'blockQuote', 'uploadImage', '|', 'bulletedList', 'numberedList', '|', 'alignment:left', 'alignment:right', '|', 'insertTable', '|', 'undo', 'redo'],
    alignment: {
        options: ['left', 'right']
    },
    placeholderProps: {
        types: ['FROM_FULLNAME', 'FROM_FIRSTNAME', 'FROM_LASTNAME', 'EMAIL_SUBJECT', 'FROM_EMAIL', 'TO_FULLNAME', 'TO_FIRSTNAME', 'FIRST_MEETING_DATE', 'FIRST_MEETING_START_TIME', 'FIRST_MEETING_END_TIME', 'FIRST_MEETING_DATE_AND_TIME', 'PRODUCT_NAME', 'PRODUCT_LOCATION', 'PRODUCT_ADDRESS', 'PRODUCT_ZIP_CODE', 'PRODUCT_CITY', 'NETWORK_DIRECTOR_1_FIRSTNAME', 'NETWORK_DIRECTOR_1_FULLNAME', 'NETWORK_DIRECTOR_2_FIRSTNAME', 'NETWORK_DIRECTOR_2_FULLNAME'],
    },
    placeholderBrackets: {
        open: "[",
        close: "]",
    },
    table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
    },
    simpleUpload: {
        // The URL that the images are uploaded to.
        uploadUrl: BASE_DOMAIN_URL + '/api/events/uploadimage',
        // Enable the XMLHttpRequest.withCredentials property.
        withCredentials: false,
        // Headers sent along with the XMLHttpRequest to the upload server.
        headers: {
            Authorization: localStorage.getItem('token')
        }
    },
};

function MakeNoteModal({open, handleClose, handleSubmit: onParentSubmit}) {
    const [editor, setEditor] = React.useState(null);

    const formik = useFormik({
        initialValues: {
            note: '',
            actionType: '',
        },
        validate: values => {
            const errors = {};

            if (!values.note) {
                errors.note = 'Required';
            }

            return errors;
        },
        onSubmit: (values, {setSubmitting}) => {
            onParentSubmit(values);
            setSubmitting(false);
            handleClose();
        },
    });
    const {resetForm, submitForm, setTouched, setFieldValue} = formik;

    React.useEffect(() => {

        if (!open) {
            resetForm();
            editor?.setData('');
        }

    }, [open, editor, resetForm]);

    const handleActionClick = actionType => {
        setFieldValue('actionType', actionType, false);
        setTouched({note: true});
        submitForm();
    };

    return (
        <Dialog maxWidth="lg" fullWidth open={open} onClose={handleClose}>
            <DialogTitle>Make Note</DialogTitle>
            <DialogContent>
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{'& .MuiTextField-root': {mt: 1, width: '100%'}}}>
                        <InputLabel
                            htmlFor="note"
                            sx={{
                                color: formik.touched.note && formik.errors.note ? 'red' : 'inherit',
                                mt: 2,
                            }}
                        >
                            Note
                        </InputLabel>
                        <CKEditor
                            id="note"
                            name="note"
                            editor={ClassicEditor}
                            config={editorConfiguration}
                            data={formik.values.note}
                            onReady={setEditor}
                            onChange={(_e, ed) => {
                                formik.setFieldValue('note', ed.getData());
                            }}
                        />
                        {formik.touched.note && formik.errors.note && (
                            <Typography variant="body2" color="error">
                                {formik.errors.note}
                            </Typography>
                        )}
                    </Box>

                    <Box display="flex" justifyContent="center" mt={2}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleActionClick(MISSED_CALL)}
                            sx={{mr: 1}}
                        >
                            Missed Call
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleActionClick(COMPLETED_CALL)}
                            sx={{mr: 1}}
                        >
                            Complete Call
                        </Button>
                        <Button variant="contained" color="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default MakeNoteModal;
